import { TranslateFn } from 'src/localization/translate'

import { KybFieldError } from './paymentsKyb.validation'

/**
 * Map a validation error code to a user-facing message.
 *
 * `*_REQUIRED` codes used to return nothing, because a pristine form was gated
 * by a disabled Next instead. Now that Next is always actionable and errors
 * surface only after an advance attempt, an empty required field has to say so
 * — otherwise pressing Next refuses to move with nothing on screen explaining
 * why.
 */
export function kybInvalidMessage(
  translate: TranslateFn,
  code: KybFieldError | undefined,
): string | undefined {
  switch (code) {
    // ── required ──────────────────────────────────────────────────────
    case 'LEGAL_NAME_REQUIRED':
    case 'LEGAL_STRUCTURE_REQUIRED':
    case 'DATE_FORMED_REQUIRED':
    case 'BUSINESS_DESCRIPTION_REQUIRED':
    case 'INTENDED_USE_REQUIRED':
    case 'ACTIVITY_VOLUME_REQUIRED':
    case 'EIN_REQUIRED':
    case 'EMAIL_REQUIRED':
    case 'PHONE_REQUIRED':
    case 'OWNER_FIRST_NAME_REQUIRED':
    case 'OWNER_LAST_NAME_REQUIRED':
    case 'OWNER_DOB_REQUIRED':
    case 'OWNER_EMAIL_REQUIRED':
    case 'OWNER_SSN_REQUIRED':
    case 'OWNER_PHONE_REQUIRED':
    case 'OWNER_OWNERSHIP_REQUIRED':
    case 'ADDRESS_LINE1_REQUIRED':
    case 'ADDRESS_LOCALITY_REQUIRED':
    case 'ADDRESS_REGION_REQUIRED':
    case 'ADDRESS_POSTAL_CODE_REQUIRED':
    case 'ADDRESS_COUNTRY_REQUIRED':
      return translate('This field is required.')
    case 'EIN_LETTER_REQUIRED':
      return translate('Attach your EIN confirmation letter.')

    // ── malformed ─────────────────────────────────────────────────────
    case 'DATE_FORMED_INVALID':
    case 'OWNER_DOB_INVALID':
      return translate('Enter a valid date.')
    case 'DATE_FORMED_FUTURE':
      return translate('Date of incorporation cannot be in the future.')
    case 'OWNER_DOB_UNDERAGE':
      return translate('This person must be at least 18 years old.')
    case 'ACTIVITY_VOLUME_INVALID':
      return translate('Enter a positive whole dollar amount.')
    case 'EIN_INVALID':
      return translate('Enter a valid 9-digit EIN.')
    case 'EMAIL_INVALID':
    case 'OWNER_EMAIL_INVALID':
      return translate('Enter a valid email address.')
    case 'WEBSITE_INVALID':
      return translate('Enter a valid website.')
    case 'PHONE_INVALID':
    case 'OWNER_PHONE_INVALID':
      return translate('Enter a valid phone number, e.g. (212) 555-0123.')
    case 'OWNER_OWNERSHIP_INVALID':
      return translate('Enter a percentage between 25 and 100.')
    case 'OWNER_SSN_INVALID':
      return translate('Enter a valid 9-digit SSN.')
    default:
      return undefined
  }
}
